import { useSearchMetadataFields } from '@modules/ManageUnifiedFields/hooks';
import { SelectedMetadataFields } from '@modules/ManageUnifiedFields/types';
import {
  AddRounded,
  CheckRounded,
  IndeterminateCheckBoxRounded,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MetadataDataType } from 'generated/graphql';
import { SyntheticEvent, useState } from 'react';
import { useDebounce } from 'react-use';
import { SelectedMetadataFieldsList } from '../SelectedMetadataFieldsList';
import { SourceChip } from '../SourceChip';

interface SelectMetadataFieldAutoCompleteProps {
  value?: SelectedMetadataFields;
  setReferencedKeys: (referencedKeys: string[]) => void;
  metadataType?: MetadataDataType;
}

export const SelectMetadataFieldAutoComplete = ({
  value = [],
  setReferencedKeys,
  metadataType,
}: SelectMetadataFieldAutoCompleteProps) => {
  const [selectedMetadataField, setSelectedMetadataField] =
    useState<SelectedMetadataFields>(value);
  const [searchQuery, setSearchQuery] = useState('');

  const [queryMetadataFields, { searchMetadataFields, loading }] =
    useSearchMetadataFields();

  useDebounce(
    () => {
      if (!searchQuery) {
        return;
      }

      queryMetadataFields({
        query: searchQuery,
      });
    },
    250,
    [searchQuery],
  );

  const onMetadataSelect = (
    _: SyntheticEvent,
    value: SelectedMetadataFields,
  ) => {
    const newValue = value[value.length - 1];

    if (!newValue && value.length === 0) {
      setSelectedMetadataField([]);
      return;
    }

    setReferencedKeys(value.map((field) => field.field));
    setSelectedMetadataField(value);
  };

  const removeSelectedMetadata = (metadata: SelectedMetadataFields[number]) => {
    const newSelectedMetadata = selectedMetadataField.filter(
      (field) => field.field !== metadata.field,
    );

    setReferencedKeys(newSelectedMetadata.map((field) => field.field));
    setSelectedMetadataField(newSelectedMetadata);
  };

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const renderActionButton = (
    option: SelectedMetadataFields[number],
    selected: boolean,
  ) => {
    if (!selected) {
      return (
        <Button color="primary" variant="text" startIcon={<AddRounded />}>
          Add
        </Button>
      );
    }

    if (hoveredItem === option.field) {
      return (
        <Button color="error" startIcon={<IndeterminateCheckBoxRounded />}>
          Remove
        </Button>
      );
    }

    return (
      <Button color="success" startIcon={<CheckRounded />}>
        Added
      </Button>
    );
  };

  return (
    <>
      <Autocomplete
        disablePortal
        filterOptions={(x) => x}
        id="select-unified-metadata-field"
        value={selectedMetadataField}
        onInputChange={(_, value, reason) => {
          setSearchQuery(value);
        }}
        renderOption={(props, option, { selected }) => {
          return (
            <ListItem
              disablePadding
              {...props}
              key={option.field}
              onMouseEnter={() => setHoveredItem(option.field)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <ListItemText
                primary={
                  <Stack direction="column" alignItems="flex-start" spacing={1}>
                    <Stack spacing={1} alignItems="center" direction="row">
                      <Box
                        component="img"
                        width="20px"
                        height="auto"
                        src={option.sourceIconURL}
                        alt={`icon of ${option.source}`}
                        loading="lazy"
                      />
                      <Typography variant="inherit">{option.label}</Typography>
                    </Stack>
                    <SourceChip source={option.source} />
                  </Stack>
                }
              />

              <ListItemIcon>
                {renderActionButton(option, selected)}
              </ListItemIcon>
            </ListItem>
          );
        }}
        noOptionsText={
          searchQuery
            ? 'No matching metadata fields'
            : 'Start typing to search metadata fields'
        }
        loading={loading}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) =>
          option.field === value.field && option.source === value.source
        }
        options={searchMetadataFields ?? []}
        multiple
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search for metadata fields you want to unify..."
            onKeyDown={(event) => {
              // disable clearing selected items when backspace is pressed
              if (event.key === 'Backspace') {
                event.stopPropagation();
              }
            }}
          />
        )}
        fullWidth
        disableCloseOnSelect
        onChange={onMetadataSelect}
        renderTags={() => null}
        clearIcon={null}
      />
      <SelectedMetadataFieldsList
        selectedMetadataField={selectedMetadataField}
        removeSelectedMetadata={removeSelectedMetadata}
        metadataType={metadataType}
      />
    </>
  );
};
